import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatDate } from '@halo/web-lib';
import {
  MessageSquare,
  Trash2,
  Loader2,
  Users,
  Search,
  X,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Store,
} from 'lucide-react';

// ─── Types ──────────────────────────────────────

interface AdminSlackInstallation {
  id: string;
  teamId: string;
  teamName: string;
  scopeType: 'store' | 'agency';
  storeId: number | null;
  storeName: string | null;
  agencyId: number | null;
  agencyName: string | null;
  installedAt: string;
}

interface AdminSlackUserMapping {
  id: string;
  teamId: string;
  slackUserId: string;
  haloUserId: string;
  haloUserEmail: string | null;
  createdAt: string;
}

type ActiveTab = 'installations' | 'user-mappings';

// ─── Toast ──────────────────────────────────────

function useToast() {
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  return { toast, showToast: setToast };
}

// ─── Main Page ──────────────────────────────────

export function SlackPage() {
  const queryClient = useQueryClient();
  const { toast, showToast } = useToast();
  const [activeTab, setActiveTab] = useState<ActiveTab>('installations');
  const [search, setSearch] = useState('');
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  // ─── Installations query ───────────────────────

  const { data: installations, isLoading: installationsLoading } = useQuery({
    queryKey: ['admin', 'slack', 'installations'],
    queryFn: async ({ signal }) => {
      const res = await api.get<AdminSlackInstallation[]>('/api/v1/admin/slack/installations', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load Slack installations');
      return res.data;
    },
  });

  // ─── User mappings query ───────────────────────

  const { data: userMappings, isLoading: mappingsLoading } = useQuery({
    queryKey: ['admin', 'slack', 'user-mappings'],
    queryFn: async ({ signal }) => {
      const res = await api.get<AdminSlackUserMapping[]>('/api/v1/admin/slack/user-mappings', { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed to load Slack user mappings');
      return res.data;
    },
    enabled: activeTab === 'user-mappings',
  });

  // ─── Revoke mutation ───────────────────────────

  const revokeInstallation = useMutation({
    mutationFn: async (id: string) => {
      const res = await api.delete(`/api/v1/admin/slack/installations/${id}`);
      if (!res.success) throw new Error(res.error ?? 'Failed to revoke installation');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'slack', 'installations'] });
      showToast({ message: 'Slack installation revoked', type: 'success' });
      setConfirmRevokeId(null);
    },
    onError: (err) => {
      showToast({ message: err instanceof Error ? err.message : 'Failed to revoke installation', type: 'error' });
      setConfirmRevokeId(null);
    },
  });

  // ─── Summary ──────────────────────────────────

  const summary = useMemo(() => ({
    workspaces: installations?.length ?? 0,
    mappings: userMappings?.length ?? 0,
  }), [installations, userMappings]);

  // ─── Filtered installations ────────────────────

  const filteredInstallations = useMemo(() => {
    const list = installations ?? [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (i) =>
        i.teamName.toLowerCase().includes(q) ||
        i.teamId.toLowerCase().includes(q) ||
        (i.storeName?.toLowerCase().includes(q) ?? false) ||
        (i.agencyName?.toLowerCase().includes(q) ?? false),
    );
  }, [installations, search]);

  // ─── Filtered user mappings ────────────────────

  const filteredMappings = useMemo(() => {
    const list = userMappings ?? [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (m) =>
        m.slackUserId.toLowerCase().includes(q) ||
        m.teamId.toLowerCase().includes(q) ||
        m.haloUserId.toLowerCase().includes(q) ||
        (m.haloUserEmail?.toLowerCase().includes(q) ?? false),
    );
  }, [userMappings, search]);

  const isLoading = activeTab === 'installations' ? installationsLoading : mappingsLoading;

  return (
    <div>
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
          toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
        }`}>
          {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {toast.message}
          <button onClick={() => showToast(null)} className="ml-2 opacity-70 hover:opacity-100">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-0.5">Slack</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {summary.workspaces} workspace{summary.workspaces !== 1 ? 's' : ''} installed
            {summary.mappings > 0 && (
              <span className="text-indigo-600 dark:text-indigo-400"> &middot; {summary.mappings} user mapping{summary.mappings !== 1 ? 's' : ''}</span>
            )}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <button
          onClick={() => setActiveTab('installations')}
          className={`bg-white dark:bg-gray-900 border rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm ${
            activeTab === 'installations' ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'
          }`}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <MessageSquare className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">Workspaces</span>
          </div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">{summary.workspaces}</div>
        </button>
        <button
          onClick={() => setActiveTab('user-mappings')}
          className={`bg-white dark:bg-gray-900 border rounded-lg px-3 py-2 text-left transition-all hover:shadow-sm ${
            activeTab === 'user-mappings' ? 'border-gray-900 ring-1 ring-gray-900' : 'border-gray-200'
          }`}
        >
          <div className="flex items-center gap-1.5 mb-1">
            <Users className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
            <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wide">User Mappings</span>
          </div>
          <div className="text-lg font-semibold text-indigo-600 dark:text-indigo-400">{summary.mappings}</div>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-4 border-b border-gray-200 dark:border-gray-800">
        {(['installations', 'user-mappings'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => { setActiveTab(tab); setSearch(''); }}
            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === tab
                ? 'border-gray-900 text-gray-900'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700'
            }`}
          >
            {tab === 'installations' ? 'Installations' : 'User Mappings'}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={
            activeTab === 'installations'
              ? 'Search by workspace name, team ID, store, or agency...'
              : 'Search by Slack user ID, team ID, or Halo user...'
          }
          className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 dark:border-gray-800 rounded-lg bg-white dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-900"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="w-4 h-4 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300" />
          </button>
        )}
      </div>

      {/* Content */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : activeTab === 'installations' ? (
        <InstallationsTab
          installations={filteredInstallations}
          totalCount={installations?.length ?? 0}
          search={search}
          confirmRevokeId={confirmRevokeId}
          onConfirmRevoke={setConfirmRevokeId}
          onRevoke={(id) => revokeInstallation.mutate(id)}
          isPending={revokeInstallation.isPending}
          pendingId={revokeInstallation.variables}
        />
      ) : (
        <UserMappingsTab
          mappings={filteredMappings}
          totalCount={userMappings?.length ?? 0}
          search={search}
        />
      )}
    </div>
  );
}

// ─── Installations Tab ───────────────────────────

interface InstallationsTabProps {
  readonly installations: AdminSlackInstallation[];
  readonly totalCount: number;
  readonly search: string;
  readonly confirmRevokeId: string | null;
  readonly onConfirmRevoke: (id: string | null) => void;
  readonly onRevoke: (id: string) => void;
  readonly isPending: boolean;
  readonly pendingId: string | undefined;
}

function InstallationsTab({
  installations,
  totalCount,
  search,
  confirmRevokeId,
  onConfirmRevoke,
  onRevoke,
  isPending,
  pendingId,
}: InstallationsTabProps) {
  if (totalCount === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-5 py-12 text-center">
        <MessageSquare className="w-8 h-8 text-gray-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">No Slack installations</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 max-w-sm mx-auto">
          Slack workspaces will appear here after stores or agencies install the Halo Slack app.
        </p>
      </div>
    );
  }

  if (installations.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
        <p className="text-sm text-gray-400 dark:text-gray-500">No installations matching &ldquo;{search}&rdquo;</p>
        <button onClick={() => onConfirmRevoke(null)} className="text-xs text-blue-600 dark:text-blue-400 mt-1 hover:underline">
          Clear search
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100">
        {installations.map((installation) => (
          <InstallationRow
            key={installation.id}
            installation={installation}
            confirmRevokeId={confirmRevokeId}
            onConfirmRevoke={onConfirmRevoke}
            onRevoke={onRevoke}
            isPending={isPending}
            pendingId={pendingId}
          />
        ))}
      </div>
      {search && (
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 text-center">
          Showing {installations.length} of {totalCount} installations
        </p>
      )}
    </>
  );
}

// ─── Installation Row ────────────────────────────

interface InstallationRowProps {
  readonly installation: AdminSlackInstallation;
  readonly confirmRevokeId: string | null;
  readonly onConfirmRevoke: (id: string | null) => void;
  readonly onRevoke: (id: string) => void;
  readonly isPending: boolean;
  readonly pendingId: string | undefined;
}

function InstallationRow({
  installation,
  confirmRevokeId,
  onConfirmRevoke,
  onRevoke,
  isPending,
  pendingId,
}: InstallationRowProps) {
  const isRevoking = isPending && pendingId === installation.id;
  const isConfirming = confirmRevokeId === installation.id;

  return (
    <div className="flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors group">
      <div className="flex items-center gap-3 min-w-0">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
          installation.scopeType === 'agency' ? 'bg-purple-50' : 'bg-blue-50'
        }`}>
          {installation.scopeType === 'agency' ? (
            <Building2 className="w-4 h-4 text-purple-600 dark:text-purple-400" />
          ) : (
            <Store className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          )}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{installation.teamName}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${
              installation.scopeType === 'agency'
                ? 'bg-purple-50 text-purple-700 dark:text-purple-400 border-purple-200'
                : 'bg-blue-50 text-blue-700 dark:text-blue-400 border-blue-200'
            }`}>
              {installation.scopeType === 'agency' ? 'Agency' : 'Store'}
            </span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
            <code className="font-mono text-gray-600 dark:text-gray-300">{installation.teamId}</code>
            <span className="text-gray-300">|</span>
            {installation.scopeType === 'agency' ? (
              <span>{installation.agencyName ?? `Agency #${installation.agencyId}`}</span>
            ) : (
              <span>{installation.storeName ?? `Store #${installation.storeId}`}</span>
            )}
            <span className="text-gray-300">|</span>
            <span>Installed {formatDate(installation.installedAt)}</span>
          </div>
        </div>
      </div>

      <div className="shrink-0 ml-4">
        {isConfirming ? (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-red-600 dark:text-red-400 font-medium">Revoke?</span>
            <button
              onClick={() => onRevoke(installation.id)}
              disabled={isRevoking}
              className="text-[10px] px-2 py-1 rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {isRevoking ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}
            </button>
            <button
              onClick={() => onConfirmRevoke(null)}
              className="text-[10px] px-2 py-1 rounded border border-gray-200 dark:border-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-950 transition-colors"
            >
              No
            </button>
          </div>
        ) : (
          <button
            onClick={() => onConfirmRevoke(installation.id)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-900/50 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Revoke
          </button>
        )}
      </div>
    </div>
  );
}

// ─── User Mappings Tab ───────────────────────────

interface UserMappingsTabProps {
  readonly mappings: AdminSlackUserMapping[];
  readonly totalCount: number;
  readonly search: string;
}

function UserMappingsTab({ mappings, totalCount, search }: UserMappingsTabProps) {
  if (totalCount === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 px-5 py-12 text-center">
        <Users className="w-8 h-8 text-gray-300 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">No user mappings</p>
        <p className="text-xs text-gray-400 dark:text-gray-500 max-w-sm mx-auto">
          User mappings connect Slack identities to Halo accounts. They are created when users link their Slack account.
        </p>
      </div>
    );
  }

  if (mappings.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-8 text-center">
        <p className="text-sm text-gray-400 dark:text-gray-500">No mappings matching &ldquo;{search}&rdquo;</p>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100">
        {mappings.map((mapping) => (
          <UserMappingRow key={mapping.id} mapping={mapping} />
        ))}
      </div>
      {search && (
        <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 text-center">
          Showing {mappings.length} of {totalCount} mappings
        </p>
      )}
    </>
  );
}

// ─── User Mapping Row ────────────────────────────

function UserMappingRow({ mapping }: { readonly mapping: AdminSlackUserMapping }) {
  return (
    <div className="flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-indigo-50 dark:bg-indigo-950/40">
          <Users className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-gray-100 font-mono">{mapping.slackUserId}</span>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>Team: <code className="font-mono text-gray-600 dark:text-gray-300">{mapping.teamId}</code></span>
            <span className="text-gray-300">|</span>
            <span>
              Halo:{' '}
              {mapping.haloUserEmail
                ? <span className="text-gray-700 dark:text-gray-200">{mapping.haloUserEmail}</span>
                : <code className="font-mono text-gray-600 dark:text-gray-300">{mapping.haloUserId}</code>
              }
            </span>
            <span className="text-gray-300">|</span>
            <span>Linked {formatDate(mapping.createdAt)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Loading Skeleton ────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-3">
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 divide-y divide-gray-100">
        {Array.from({ length: 4 }, (_, i) => (
          <div key={i} className="px-5 py-4 animate-pulse flex items-center gap-3">
            <div className="w-9 h-9 bg-gray-200 dark:bg-gray-700 rounded-lg shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-40" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-12" />
              </div>
              <div className="flex items-center gap-2">
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-28 font-mono" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-24" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-20" />
              </div>
            </div>
            <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded-lg w-20 shrink-0" />
          </div>
        ))}
      </div>
    </div>
  );
}
